import type { ApiRentalEscrowTerms, ApiDeliveryEscrowTerms } from 'src/types/escrow';
import type { TxProgressInfo, ContractCallArg, UseWalletUIResult } from 'src/blockchain';

import { ReceiptTimeoutError } from 'src/blockchain/receipt';

import { isUserRejection } from './walletErrors';
import { getApiErrorCode, getApiErrorMessage } from './listingAvailability';

/**
 * The buyer's own wallet creates the escrow contract (api#380, spec §2.9).
 *
 * On Base the platform no longer deploys the clone of a deal. It signs a *terms sheet* — the
 * factory's config, a deadline, an EIP-712 signature — and the buyer's wallet sends
 * `create*Deal(config, deadline, signature)` to the factory itself. The payment is then three
 * wallet prompts instead of two: Create escrow → approve → fund (or pay). This module is the whole
 * of that sequence, apart from rendering it: which arguments go to the factory, what is remembered
 * between the prompts, what happens when a step fails, and how the api is told the contract exists.
 *
 * It is pure on purpose — the wallet, the api calls, the storage and the clock all come in as
 * parameters — so every branch below is a unit test in the node environment, and the two pay
 * dialogs (delivery, rental) are thin: they prepare the vertical's pieces and call `payEscrow`.
 *
 * What it must never do:
 * - **create the contract twice.** The transaction hash goes into `localStorage` the moment the
 *   wallet hands it over, before the receipt; a reload, a failed report or a closed tab finds it
 *   there and *reports* that hash instead of asking for a second signature. The factory refuses a
 *   second creation anyway (`DealAlreadyExists`), and the call is simulated before it is signed, so
 *   the stash is the buyer's comfort, not the money's last line — but a buyer who is asked to sign
 *   the same thing twice is a buyer who stops trusting the screen.
 * - **approve a number this screen computed.** Steps 2–3 read `getTotalRequired()` off the created
 *   clone, as the two-prompt flow always has; the sheet's amounts are never summed here.
 * - **decide anything the sheet decides.** The tuple is assembled from the sheet's fields *by
 *   name*; a storefront that reordered or recomputed one field would produce a signature mismatch,
 *   which the factory refuses — the right outcome, but one the buyer paid a simulation for.
 */

// ----------------------------------------------------------------------
// The words (spec §2.9, exact). The three-prompt sentence itself carries the amount in bold and
// lives in the dialogs; these two are the lines around it.
// ----------------------------------------------------------------------

/** Under the three-prompt sentence, on an EVM chain. */
export const ESCROW_ETH_BALANCE_HINT =
  'You need a small ETH balance on Base for the three transactions.';

/** Under the invoice total, on an EVM chain: what the total does not include. */
export const ESCROW_NETWORK_FEES_NOTE =
  'Network fees for the three transactions are paid from your wallet on Base, usually under a cent each.';

/** The name of the creation step in the progress panel. */
export const CREATE_ESCROW_STEP_LABEL = 'Create escrow';

// ----------------------------------------------------------------------
// The stash: a sent creation transaction, remembered until the api has it.
// ----------------------------------------------------------------------

export const escrowCreateTxStashKey = (dealId: string): string => `escrow-create-tx:${dealId}`;

/** What the flow needs of `localStorage`; a test passes a Map-backed fake. */
export type StashStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * `localStorage`, if this browser lets us have it. Private mode and a storage quota throw on
 * access; the flow then runs without a stash — the protection degrades to the factory's own
 * `DealAlreadyExists`, never to a double payment.
 */
const browserStash = (): StashStore | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export const readEscrowStash = (
  dealId: string,
  store: StashStore | null = browserStash()
): string | null => {
  try {
    return store?.getItem(escrowCreateTxStashKey(dealId)) || null;
  } catch {
    return null;
  }
};

export const writeEscrowStash = (
  dealId: string,
  txHash: string,
  store: StashStore | null = browserStash()
): void => {
  try {
    store?.setItem(escrowCreateTxStashKey(dealId), txHash);
  } catch {
    /* ignore — best effort */
  }
};

export const clearEscrowStash = (
  dealId: string,
  store: StashStore | null = browserStash()
): void => {
  try {
    store?.removeItem(escrowCreateTxStashKey(dealId));
  } catch {
    /* ignore — best effort */
  }
};

// ----------------------------------------------------------------------
// The factory call: signature, tuple by field name, gas budget, refusals.
// ----------------------------------------------------------------------

/**
 * The generation-2 factories' creation functions, as human-readable signatures with the config
 * tuple written inline (onchain-evm#16; the api's `DeliveryFactory.json` / `RentalFactory.json`
 * are the source). The field names here are documentation — the encoder takes the tuple by
 * position — and the order **is** the contract's: `escrowCreation.spec.ts` encodes a sheet
 * through these and compares the bytes with the factory ABI.
 */
export const CREATE_DELIVERY_DEAL_SIGNATURE =
  'createDeliveryDeal((address buyer, address dealer, address logist, address platform, address paymentToken, ' +
  'uint256 priceAmount, uint256 deliveryAmount, uint256 feeAmount, string dealId) config, uint256 deadline, bytes signature)';

export const CREATE_RENTAL_DEAL_SIGNATURE =
  'createRentalDeal((address renter, address rentier, address logistics, address platform, address paymentToken, ' +
  'uint256 depositAmount, uint256 rentalAmount, uint256 rentalPeriod, uint256[4] dailyRates, uint256 payDeadline, ' +
  'uint256 bookedEnd, string vehicleClass, string dealId) config, uint256 deadline, bytes signature)';

/**
 * The factories' refusals, so a simulation answers by name. Shared by both factories; a name a
 * factory does not have is simply never raised by it.
 */
export const FACTORY_ERRORS: readonly string[] = [
  'NotTheBuyer()',
  'NotTheRenter()',
  'DealAlreadyExists()',
  'TermsExpired()',
  'InvalidTermsSignature()',
  'InvalidConfig()',
  'TokenNotApproved()',
  'EnforcedPause()',
];

/**
 * Gas budgets for the creation, in gas units. Measured on Base Sepolia in onchain-evm#16: 479 912
 * for a delivery deal, 685 169 for a rental; the budget is about 1.4× that. The network refunds
 * what is not used, so the margin costs nothing — while an estimate from a lagging node once took
 * the cheap revert path and burnt the fee for a transaction that then failed (api#380, PR-1).
 */
export const CREATE_DELIVERY_DEAL_GAS = '700000';
export const CREATE_RENTAL_DEAL_GAS = '1000000';

/** The three arguments of `createDeliveryDeal`, the config taken from the sheet **by field name**. */
export const deliveryCreateArgs = (terms: ApiDeliveryEscrowTerms): readonly ContractCallArg[] => {
  const { config } = terms;

  return [
    [
      config.buyer,
      config.dealer,
      config.logist,
      config.platform,
      config.paymentToken,
      config.priceAmount,
      config.deliveryAmount,
      config.feeAmount,
      config.dealId,
    ],
    String(terms.deadline),
    terms.signature,
  ];
};

/** The three arguments of `createRentalDeal`, the config taken from the sheet **by field name**. */
export const rentalCreateArgs = (terms: ApiRentalEscrowTerms): readonly ContractCallArg[] => {
  const { config } = terms;

  return [
    [
      config.renter,
      config.rentier,
      config.logistics,
      config.platform,
      config.paymentToken,
      config.depositAmount,
      config.rentalAmount,
      config.rentalPeriod,
      [config.dailyRates[0], config.dailyRates[1], config.dailyRates[2], config.dailyRates[3]],
      config.payDeadline,
      config.bookedEnd,
      config.vehicleClass,
      config.dealId,
    ],
    String(terms.deadline),
    terms.signature,
  ];
};

/** Everything the wallet needs to create the contract — one vertical's sheet, prepared. */
export interface EscrowCreateCall {
  factoryAddress: string;
  chainId: number;
  signature: string;
  args: readonly ContractCallArg[];
  gasLimit: string;
}

export const deliveryCreateCall = (terms: ApiDeliveryEscrowTerms): EscrowCreateCall => ({
  factoryAddress: terms.factoryAddress,
  chainId: terms.chainId,
  signature: CREATE_DELIVERY_DEAL_SIGNATURE,
  args: deliveryCreateArgs(terms),
  gasLimit: CREATE_DELIVERY_DEAL_GAS,
});

export const rentalCreateCall = (terms: ApiRentalEscrowTerms): EscrowCreateCall => ({
  factoryAddress: terms.factoryAddress,
  chainId: terms.chainId,
  signature: CREATE_RENTAL_DEAL_SIGNATURE,
  args: rentalCreateArgs(terms),
  gasLimit: CREATE_RENTAL_DEAL_GAS,
});

/**
 * The name of the custom error a contract call reverted with, read off whatever the wallet layer
 * threw: viem's `ContractFunctionRevertedError` carries it as `data.errorName` somewhere down the
 * `cause` chain, and spells it in the message as `Error: Name()` as well. `null` when the failure
 * was not a named revert (a rejection in the wallet, a network error, an unknown error).
 */
export const revertErrorName = (error: unknown): string | null => {
  let current: unknown = error;

  for (let depth = 0; depth < 8 && current && typeof current === 'object'; depth += 1) {
    const candidate = current as {
      data?: { errorName?: unknown };
      cause?: unknown;
      message?: unknown;
    };
    const named = candidate.data?.errorName;

    if (typeof named === 'string' && named) {
      return named;
    }

    if (typeof candidate.message === 'string') {
      // viem spells a decoded custom error on its own line, `Error: Name()`; anchored to that line.
      const match = /(?:^|\n)\s*Error:\s*([A-Za-z_]\w*)\(/.exec(candidate.message);

      if (match) {
        return match[1];
      }
    }

    current = candidate.cause;
  }

  return null;
};

/** The buyer's sentence for a factory's refusal; `null` for an error this module does not know. */
export const describeFactoryRefusal = (errorName: string | null): string | null => {
  switch (errorName) {
    case 'NotTheBuyer':
    case 'NotTheRenter':
      return 'Connect the wallet linked to this account.';
    case 'TermsExpired':
      return 'The payment window for this order has closed. Reopen the order to see its status.';
    case 'InvalidTermsSignature':
    case 'InvalidConfig':
      return 'The terms of this order could not be verified on the network. Please contact support.';
    case 'EnforcedPause':
      return 'Escrow creation is paused on this network right now. Please try again later.';
    default:
      return null;
  }
};

// ----------------------------------------------------------------------
// Reporting the creation to the api.
// ----------------------------------------------------------------------

/** The api's answers to `escrow-created` that mean "ask again in a moment". */
export const ESCROW_REPORT_RETRY_CODES: readonly string[] = [
  // The receipt is there but not `minConfirmations` deep yet (two blocks on Base, about four seconds).
  'ESCROW_TX_PENDING',
  // The api's node has not seen the transaction our node already mined — a block of lag.
  'ESCROW_TX_NOT_FOUND',
  // The api could not reach the chain at all; the transaction is unaffected.
  'ESCROW_CHAIN_UNREACHABLE',
];

/** How often and how long the report is retried: every ~3 s for a minute. */
export const ESCROW_REPORT_RETRY_MS = 3_000;
export const ESCROW_REPORT_MAX_ATTEMPTS = 20;

/** How long a stashed transaction from before a reload is waited for, before telling the buyer it is still pending. */
export const ESCROW_STASH_WAIT_MS = 90_000;

/**
 * The report could not be completed, though the creation transaction is out. The screen shows
 * `message`; `code` is the api's last answer, or `ESCROW_REPORT_TIMEOUT` when the retries ran out.
 */
export class EscrowReportError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'EscrowReportError';
  }
}

export const escrowNotReportedMessage = (txHash: string): string =>
  `Escrow created (tx ${txHash}) but not reported yet — reopen this screen, do not create it again.`;

export const ESCROW_NOT_AWAITING_MESSAGE =
  'This order is no longer awaiting payment. Reopen it to see its status.';

export const escrowDroppedMessage = (txHash: string): string =>
  `The network does not know the remembered transaction (tx ${txHash}) — it was never mined. Nothing was created; pay again to create the escrow.`;

/**
 * The remembered creation will never mine: the buyer cancelled it in the wallet (a transfer of
 * nothing to themselves took its nonce), or another transaction did. Said while the flow goes
 * straight on to create afresh (app#376).
 */
export const escrowStashReplacedNotice = (
  reason: 'cancelled' | 'replaced',
  txHash: string
): string =>
  reason === 'cancelled'
    ? `The remembered transaction (tx ${txHash}) was cancelled in your wallet — nothing was created. Creating the escrow again.`
    : `Another transaction from your wallet took the remembered transaction's place (tx ${txHash}) — nothing was created. Creating the escrow again.`;

/** The run was given up on (the wallet prompt timed out) and nobody listens: stop before the next step. */
export class EscrowAbortedError extends Error {
  constructor() {
    super('The payment was interrupted. Nothing further was signed.');
    this.name = 'EscrowAbortedError';
  }
}

/**
 * The api's final refusals of a report, and what the buyer is told when one of them means the
 * stashed hash is of no further use. Absent from this table: the retryable answers and anything
 * without a code, for which the stash is kept (`escrowNotReportedMessage`).
 */
const STASH_VERDICT: Record<string, string> = {
  ESCROW_TX_FAILED: 'The escrow was not created on the network. Please try again.',
  ESCROW_TX_WRONG_FACTORY:
    'The remembered transaction is not the escrow creation of this order. Pay again to create the escrow.',
  ESCROW_TX_NOT_THIS_DEAL:
    'The remembered transaction is not the escrow creation of this order. Pay again to create the escrow.',
  ESCROW_DEAL_NOT_AWAITING: ESCROW_NOT_AWAITING_MESSAGE,
  ESCROW_TERMS_MISSING: 'This order has no escrow terms to report. Reopen it to see its status.',
};

/** The deal as the api answers `escrow-created` and the detail: what the flow reads of it. */
export interface EscrowDealView<Terms> {
  contractAddress: string | null;
  escrowTerms?: Terms | null;
  /**
   * Whether the deal still takes a payment — `awaiting_payment` for a delivery, an unpaid and not
   * terminal booking for a rental. The dialog derives it from the status; the flow refuses to
   * approve or fund on any read that says `false`, address or no address: a clone created at the
   * edge of the window may be adopted by the expiry sweep while the deal is closed as
   * `approval_expired`, and `fund()` has no deadline of its own.
   */
  payable: boolean;
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Tells the api the hash the contract was created with, until it answers with the address.
 *
 * Resolves with the address. Rejects with `EscrowReportError` when the api refused for good (a
 * reverted or foreign transaction, a deal that is no longer awaiting one) or when the retries ran
 * out — the caller decides what the stash does then. The api is idempotent: a hash it already
 * recorded comes back with the same address.
 */
export const reportEscrowCreation = async <Terms>(
  txHash: string,
  report: (txHash: string) => Promise<EscrowDealView<Terms>>,
  options: { sleep?: (ms: number) => Promise<void>; maxAttempts?: number } = {}
): Promise<string> => {
  const { sleep = wait, maxAttempts = ESCROW_REPORT_MAX_ATTEMPTS } = options;
  let lastCode = 'ESCROW_REPORT_TIMEOUT';

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const deal = await report(txHash);

      if (deal.contractAddress) {
        // The api answers a hash it already recorded idempotently — with the deal as it is now,
        // which may be a deal the expiry sweep has closed meanwhile. An address on a deal that
        // takes no payment is a refusal, not an address.
        if (!deal.payable) {
          throw new EscrowReportError('ESCROW_DEAL_NOT_AWAITING', ESCROW_NOT_AWAITING_MESSAGE);
        }

        return deal.contractAddress;
      }

      // A 200 without an address is not an answer this api gives; read it as "not yet".
      lastCode = 'ESCROW_TX_PENDING';
    } catch (error) {
      const code = getApiErrorCode(error);

      if (code !== null && !ESCROW_REPORT_RETRY_CODES.includes(code)) {
        // A refusal the api means: the caller decides what the stash does.
        throw new EscrowReportError(
          code,
          getApiErrorMessage(error, 'The escrow could not be reported.')
        );
      }

      // A coded "not yet", or a failure with no code at all (a 5xx, the network): asked again.
      lastCode = code ?? 'ESCROW_REPORT_FAILED';
    }

    if (attempt < maxAttempts) {
      await sleep(ESCROW_REPORT_RETRY_MS);
    }
  }

  throw new EscrowReportError(lastCode, escrowNotReportedMessage(txHash));
};

// ----------------------------------------------------------------------
// The whole payment: create (if needed), report, approve, fund/pay.
// ----------------------------------------------------------------------

export interface EscrowPaymentDeps<Terms> {
  dealId: string;
  /** The deal's EVM chain id; the wallet is switched to it before every prompt. */
  chainId?: number;
  /** The deal as the dialog opened it: an address, or a sheet, or neither (then `refresh` is asked). */
  deal: EscrowDealView<Terms>;
  /** The sheet turned into the factory call of this vertical. */
  createCall: (terms: Terms) => EscrowCreateCall;
  walletUI: Pick<UseWalletUIResult, 'callContract' | 'waitForTransaction'>;
  /** `POST …/escrow-created {txHash}` — the api's answer carries the address once it has it. */
  report: (txHash: string) => Promise<EscrowDealView<Terms>>;
  /** A fresh read of the deal — the poller may have adopted the contract before this screen did. */
  refresh: () => Promise<EscrowDealView<Terms>>;
  /** `getTotalRequired()` and `paymentToken()` off the created contract — never this screen's sum. */
  readContract: (address: string) => Promise<{ totalRequired: string; paymentToken: string }>;
  /** `fund()` for a delivery, `pay()` for a rental. */
  paySignature: string;
  onProgress: (info: TxProgressInfo) => void;
  stash?: StashStore | null;
  /** The clock, injectable: the retries sleep, and a test must not. */
  sleep?: (ms: number) => Promise<void>;
  /** A line for the buyer while something unusual is handled (a stashed transaction being waited for). */
  onNotice?: (text: string) => void;
  /**
   * Set by the panel when it gave up on this run (the wallet prompt timed out) — the run cannot be
   * cancelled, so it checks before each further step and stops instead of signing on. A hash the
   * wallet hands over after that is still stashed: the next run reports it.
   */
  signal?: AbortSignal;
}

export interface EscrowPaymentResult {
  contractAddress: string;
  /** The hash of the payment (`fund()` / `pay()`). */
  paymentTxHash: string;
  /** Whether this run created the contract (three prompts) or paid into one that existed (two). */
  created: boolean;
}

/**
 * The payment, start to finish. Reads as the buyer experiences it:
 *
 * 1. **Is there a contract?** If the deal carries an address, straight to the approve. If it
 *    carries a stashed creation hash, that transaction is waited out and reported — the buyer is
 *    never asked to create again, unless the chain says that creation never will be (reverted,
 *    cancelled in the wallet, its nonce taken by another transaction): then it is forgotten and
 *    created afresh. Otherwise the deal is re-read once: the poller may have recorded the
 *    contract while this tab was closed.
 * 2. **Create.** The sheet goes to the factory as one prompt; the hash is stashed the moment it
 *    exists. A factory that answers `DealAlreadyExists` is telling us the contract is there and the
 *    api has not caught up — wait three seconds, re-read, continue. Any other named refusal
 *    becomes a sentence; a wallet rejection is left as it is (the panel says "Signature cancelled").
 * 3. **Report.** The api is told the hash until it answers with the address (a minute of retries
 *    on "pending"); the stash is cleared on that answer and *only* on that answer. A report that
 *    fails for good re-reads the deal — the poller may have adopted the contract meanwhile — and,
 *    with no address there, stops with the "do not create it again" sentence, stash intact.
 * 4. **Approve and pay**, exactly as before: the amount and the token from the contract.
 */
export const payEscrow = async <Terms>(
  deps: EscrowPaymentDeps<Terms>
): Promise<EscrowPaymentResult> => {
  const { dealId, chainId, walletUI, onProgress, sleep = wait } = deps;
  const stash = deps.stash === undefined ? browserStash() : deps.stash;
  let address: string | null = deps.deal.contractAddress;
  let terms: Terms | null | undefined = deps.deal.escrowTerms;
  let created = false;

  const progress = (offset: number, of: number) => ({ offset, of });

  const check = () => {
    if (deps.signal?.aborted) {
      throw new EscrowAbortedError();
    }
  };

  /** A read that says the deal takes no payment ends the flow, whatever else it carries. */
  const assertPayable = (view: EscrowDealView<Terms>) => {
    if (!view.payable) {
      clearEscrowStash(dealId, stash);
      throw new EscrowReportError('ESCROW_DEAL_NOT_AWAITING', ESCROW_NOT_AWAITING_MESSAGE);
    }
  };

  const reportUntilRecorded = async (
    txHash: string,
    { droppedIfNotFound = false }: { droppedIfNotFound?: boolean } = {}
  ): Promise<string> => {
    try {
      const recorded = await reportEscrowCreation(txHash, deps.report, { sleep });

      clearEscrowStash(dealId, stash);

      return recorded;
    } catch (error) {
      const code = error instanceof EscrowReportError ? error.code : 'ESCROW_REPORT_FAILED';

      // The deal has moved on (closed, or it never had a sheet): nothing here is worth continuing,
      // whatever address a fresh read might carry.
      if (code === 'ESCROW_DEAL_NOT_AWAITING' || code === 'ESCROW_TERMS_MISSING') {
        clearEscrowStash(dealId, stash);
        throw new EscrowReportError(code, STASH_VERDICT[code]);
      }

      // The api did not take the report. Before giving up, the poller may have adopted the clone
      // from the factory registry on its own — then nothing is lost and the stash can go.
      const fresh = await deps.refresh().catch(() => null);

      if (fresh?.contractAddress) {
        assertPayable(fresh);
        clearEscrowStash(dealId, stash);

        return fresh.contractAddress;
      }

      const verdict = STASH_VERDICT[code];

      if (verdict) {
        // The api has said, for good, that this hash is not the creation of this deal's contract
        // — reverted, another factory's, another deal's. Keeping the hash would only re-report the
        // same refusal forever; the factory's own `DealAlreadyExists` still refuses a second
        // creation should one be attempted.
        clearEscrowStash(dealId, stash);
        throw new EscrowReportError(code, verdict);
      }

      if (code === 'ESCROW_TX_NOT_FOUND' && droppedIfNotFound) {
        // Our own node could not find it within its wait, and neither could the api's within the
        // retries: the transaction left the mempool unmined. The hash is of no use to anyone.
        clearEscrowStash(dealId, stash);
        throw new EscrowReportError('ESCROW_TX_DROPPED', escrowDroppedMessage(txHash));
      }

      // Pending, not found yet, unreachable, a 5xx, the network: the transaction may well be
      // fine and the api behind — keep the stash and say so.
      throw new EscrowReportError(code, escrowNotReportedMessage(txHash));
    }
  };

  assertPayable(deps.deal);

  // The contract is recorded: whatever a stash remembers is finished business.
  if (address) {
    clearEscrowStash(dealId, stash);
  }

  // (1) A creation sent before — a reload mid-step, a report that failed — is finished first.
  if (!address) {
    const stashed = readEscrowStash(dealId, stash);

    if (stashed) {
      deps.onNotice?.(
        `An escrow creation was already sent for this order (tx ${stashed}). Reporting it first.`
      );
      // The panel shows the transaction, and the wallet-prompt timeout stands down: nothing is
      // being signed while a sent transaction is waited out.
      onProgress({
        phase: 'sent',
        txHash: stashed,
        step: 1,
        of: 3,
        stepLabel: CREATE_ESCROW_STEP_LABEL,
      });

      let mined: string | null = stashed;
      let localWaitFailed = false;

      if (walletUI.waitForTransaction) {
        try {
          const outcome = await walletUI.waitForTransaction({
            hash: stashed,
            chainId,
            timeoutMs: ESCROW_STASH_WAIT_MS,
          });

          if (outcome.status === 'reverted') {
            // Nothing was created; the stash would only re-report a failure forever.
            clearEscrowStash(dealId, stash);
            mined = null;
          } else if (outcome.replaced === 'cancelled' || outcome.replaced === 'replaced') {
            // Cancelled in the wallet, or another transaction took its nonce: what was mined is a
            // transfer of nothing, or someone else's business — not the creation, and the creation
            // never will be. Reporting that hash would only earn the api's refusal; say so, forget
            // it and create afresh.
            clearEscrowStash(dealId, stash);
            deps.onNotice?.(escrowStashReplacedNotice(outcome.replaced, stashed));
            mined = null;
          } else {
            mined = outcome.hash;

            if (mined !== stashed) {
              // Sped up in the wallet: the same creation, mined under another hash the chain knows.
              writeEscrowStash(dealId, mined, stash);
            }
          }
        } catch {
          // Not mined within the wait (or the node does not know it): report what we have — the
          // api answers "pending" or "not found", and the retries below decide; a hash nobody can
          // find by then was never mined and is forgotten there.
          mined = stashed;
          localWaitFailed = true;
        }
      }

      check();

      if (mined) {
        onProgress({
          phase: 'confirmed',
          txHash: mined,
          step: 1,
          of: 3,
          stepLabel: CREATE_ESCROW_STEP_LABEL,
        });
        address = await reportUntilRecorded(mined, { droppedIfNotFound: localWaitFailed });
        created = true;
      }
    }
  }

  // (1b) No address and no stash: the deal may have moved while this screen was closed. The fresh
  // read is the truth — for the address, for whether the deal still takes a payment, and for the
  // sheet: a sheet the api no longer answers (the offer withdrawn in another tab, the window
  // closed) is not sent, however validly it was signed.
  if (!address) {
    const fresh = await deps.refresh();

    check();
    assertPayable(fresh);

    if (fresh.contractAddress) {
      address = fresh.contractAddress;
      clearEscrowStash(dealId, stash);
    } else {
      terms = fresh.escrowTerms ?? null;
    }
  }

  // (2) Create the contract from the sheet — one prompt, the hash stashed as soon as it exists.
  if (!address) {
    if (!terms) {
      throw new Error('The escrow contract is not ready yet. Please try again shortly.');
    }

    const call = deps.createCall(terms);
    let createdHash: string | null = null;

    if (chainId !== undefined && call.chainId !== chainId) {
      // The sheet is bound to a chain; the deal's chain is what the wallet is switched to for the
      // approve and the payment. They are the same by construction — refuse rather than sign on
      // two networks when they are not.
      throw new Error(
        'The escrow terms of this order are for another network. Reopen the order and try again.'
      );
    }

    try {
      const result = await walletUI.callContract({
        contractAddress: call.factoryAddress,
        signature: call.signature,
        args: call.args,
        chainId: call.chainId,
        gasLimit: call.gasLimit,
        errors: FACTORY_ERRORS,
        progress: progress(0, 3),
        stepLabel: CREATE_ESCROW_STEP_LABEL,
        onProgress: (info) => {
          if (info.txHash && info.txHash !== createdHash) {
            createdHash = info.txHash;
            writeEscrowStash(dealId, info.txHash, stash);
          }
          onProgress(info);
        },
      });

      createdHash = result.hash;
      writeEscrowStash(dealId, result.hash, stash);
    } catch (error) {
      if (error instanceof ReceiptTimeoutError && createdHash) {
        // Sent, stashed, not confirmed within the wait: the next press reports the stash.
        throw new EscrowReportError(
          'ESCROW_RECEIPT_TIMEOUT',
          escrowNotReportedMessage(createdHash)
        );
      }

      const refusal = revertErrorName(error);

      if (refusal === 'DealAlreadyExists') {
        // The contract is on chain and the api has not recorded it yet (a reload without the stash,
        // a report that never arrived): give the poller a moment, then continue from the address.
        await sleep(ESCROW_REPORT_RETRY_MS);

        const fresh = await deps.refresh();

        check();
        assertPayable(fresh);

        if (!fresh.contractAddress) {
          throw new Error(
            'The escrow contract exists on the network but is not recorded yet. Please try again in a few seconds.'
          );
        }

        clearEscrowStash(dealId, stash);
        address = fresh.contractAddress;
      } else {
        const sentence = describeFactoryRefusal(refusal);

        if (sentence) {
          throw new Error(sentence);
        }

        // A refusal with a name this module does not know: viem's one-line reading of it, not its
        // multi-line dump with the raw call. A wallet rejection and everything else keep their words.
        const short = (error as { shortMessage?: unknown } | null)?.shortMessage;

        if (refusal === null && !isUserRejection(error) && typeof short === 'string' && short) {
          throw new Error(short);
        }

        throw error;
      }
    }

    check();

    // (3) Report it, until the api answers with the address.
    if (!address && createdHash) {
      address = await reportUntilRecorded(createdHash);
      created = true;
    }
  }

  if (!address) {
    throw new Error('The escrow contract is not ready yet. Please try again shortly.');
  }

  check();

  // (4) Approve exactly what the contract will pull, then fund/pay — as before.
  const { totalRequired, paymentToken } = await deps.readContract(address);
  const paid = await walletUI.callContract({
    contractAddress: address,
    signature: deps.paySignature,
    chainId,
    approve: { tokenAddress: paymentToken, spender: address, amount: totalRequired },
    progress: created ? progress(1, 3) : undefined,
    onProgress,
  });

  return { contractAddress: address, paymentTxHash: paid.hash, created };
};
